import { Helmet } from "react-helmet-async";
import { Phone } from "lucide-react";
import customerCareIcon from "@/assets/customer-care-icon.png";
import agroTrucks from "@/assets/agro-trucks.jpg";
import urgenceHeroBg from "@/assets/urgence-hero-bg.jpg";

const ServiceUrgence = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Service d'urgence 24/7 - Agroref</title>
        <meta name="description" content="Service d'intervention d'urgence disponible 24h/24 et 7j/7 pour vos systèmes de traite" />
        <link rel="preload" as="image" href={urgenceHeroBg} />
      </Helmet>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-12 md:py-20 min-h-[600px] flex items-center overflow-hidden">
          <img src={urgenceHeroBg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="sync" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl bg-black/75 backdrop-blur-md rounded-lg p-8 md:p-12">
              <div className="w-16 h-1 bg-red-600 mb-6" />
              <h1 className="text-3xl md:text-4xl md:text-5xl font-bold text-white mb-6">
                Service d&apos;urgence 24/7: Votre Support Fiable Tout le Temps
              </h1>
              <p className="text-lg text-white/80 mb-6">
                Dans le monde agricole, les imprévus sont monnaie courante. Une panne de système ou un dysfonctionnement du système de traite peut arriver à tout moment, perturbant vos activités. Chez Agro, nous comprenons l&apos;urgence de ces situations et nous sommes là pour vous aider à y faire face efficacement.
              </p>
              <p className="text-lg text-white/80 mb-8">
                Notre service d&apos;urgence 24/7 est toujours à votre disposition, prêt à fournir une assistance rapide et fiable. Quelle que soit l&apos;heure, notre équipe d&apos;experts est prête à intervenir pour garantir le bon fonctionnement de vos installations. Avec nous, vous pouvez opérer en toute confiance, sachant que vous avez un partenaire sur qui compter, à tout moment.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-accent text-white px-8 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors"
              >
                Contactez-nous dès maintenant!
              </a>
            </div>
          </div>
        </section>

        {/* Section: Les Avantages */}
        <section className="py-12 md:py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Les Avantages de Notre Service 24/7</h2>

              <div className="grid md:grid-cols-2 gap-8 text-muted-foreground">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Disponibilité constante</h3>
                    <p>
                      Avec notre service d&apos;urgence 24/7, vous avez la garantie d&apos;une assistance continue. Que ce soit pendant les heures de bureau, tard le soir ou tôt le matin, notre équipe est prête à intervenir à tout moment.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Intervention rapide</h3>
                    <p>
                      Chaque minute est précieuse en cas d&apos;urgence. Notre équipe est formée pour agir rapidement et efficacement, pour minimiser l&apos;impact des problèmes imprévus sur votre activité. Avec leur expertise et leur capacité à cerner rapidement les problèmes, nos techniciens assurent la continuité de vos opérations.
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Techniciens qualifiés</h3>
                    <p>
                      Nos techniciens sont hautement qualifiés et formés pour traiter toutes sortes de problèmes. Ils sont capables de diagnostiquer et de résoudre efficacement les problèmes, afin de vous permettre de reprendre vos activités normales dans les plus brefs délais.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Tranquillité d&apos;esprit</h3>
                    <p>
                      Avec notre service d&apos;urgence 24/7, vous pouvez opérer en toute confiance, sachant que vous avez une équipe dédiée prête à intervenir en cas de besoin. Votre tranquillité d&apos;esprit est notre priorité.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Notre Service */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Notre Service d&apos;urgence 24/7</h2>

              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    Notre Service d&apos;urgence 24/7 est conçu pour offrir une assistance rapide et fiable à tout moment de la journée ou de la nuit. Nous comprenons l&apos;importance de la disponibilité d&apos;un service de dépannage en cas de problème imprévu. C&apos;est pourquoi nous avons mis en place une équipe d&apos;experts hautement qualifiés, disponibles 24 heures sur 24 et 7 jours sur 7, pour intervenir en cas d&apos;urgence.
                  </p>
                  <p>
                    Le service comprend une assistance téléphonique en temps réel, ainsi que des interventions sur site, si nécessaire, pour résoudre rapidement les problèmes. Nos techniciens sont formés pour diagnostiquer et réparer les pannes de manière efficace et rapide, ce qui permet aux clients de reprendre leur activité normale dans les plus brefs délais.
                  </p>
                </div>

                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={agroTrucks}
                    alt="Camionnettes Agro Réfrigération"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-12 md:py-16 bg-[#333333] overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-xl font-semibold mb-6 text-white/80">Nous sommes là pour vous!</p>
            <h3 className="text-2xl font-bold mb-4 text-white">Besoin de Notre Service d&apos;Urgence 24/7?</h3>
            <p className="text-lg text-white/70 mb-8 max-w-3xl mx-auto">
              Notre équipe de service d&apos;urgence est là pour vous en cas de besoin. Nous sommes disponibles à tout moment pour résoudre les problèmes, minimiser les perturbations et assurer la continuité de vos activités. Contactez-nous dès maintenant pour une assistance rapide et fiable. Prévenez les pertes et interruptions potentielles en découvrant notre Service d&apos;urgence 24/7.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-accent text-white px-8 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors"
            >
              <Phone className="h-5 w-5" />
              Contactez-nous dès maintenant!
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceUrgence;
